import { ApplicationClientIds } from "@bp/pim-auth-constants";

/**
 * Turns raw audit events into what support reads — modelled on the
 * auth-server's Kontoaktivität (`activity-mapper.ts`) so both sides use the
 * same words: status, label, detail (product, reason, provider, target),
 * device and masked ip, "new device", unfinished logins, and support actions
 * phrased from the person's point of view.
 */

export type AuditEventLike = {
  id: string;
  action: string;
  occurredAt: string;
  actorType?: string | null;
  actorId?: string | null;
  targetType?: string | null;
  targetId?: string | null;
  metadata?: string | null;
  sessionUid?: string | null;
  loginCid?: string | null;
};

export type Metadata = Record<string, unknown>;

export type ActivityItem = {
  id: string;
  ok: boolean;
  /** A successful login step that never became a session. */
  unfinished: boolean;
  /** Someone else (support) acted on the person's account. */
  foreign: boolean;
  label: string;
  detail: string;
  /** "durch <Admin>" for support actions. */
  note: string;
  device: string;
  ip: string;
  newDevice: boolean;
  /** Calendar day in Europe/Berlin (`2026-09-15`). */
  day: string;
  time: string;
  when: string;
  occurredAt: string;
  raw: AuditEventLike;
};

export type NameMap = Map<string, string>;

// ---------------------------------------------------------------------------
// Catalog knowledge. TODO(pim-audit-log ≥ 1.1): replace this local table with
// `import { AUDIT_CATALOG } from "@bp/pim-audit-log/catalog"` once the release
// with the subpath export is out — the `ok` flags and the `trail` surface
// below mirror that catalog.
// ---------------------------------------------------------------------------

/** Events that are not "ok" — the failed / declined / reset-by-recovery kind. */
const NOT_OK = new Set([
  "LOGIN_FAILED",
  "MFA_FAILED",
  "RECOVERY_CODE_FAILED",
  "TWO_FACTOR_RESET",
  "PASSKEY_LOGIN_FAILED",
  "PASSKEY_REGISTRATION_FAILED",
  "TWO_FACTOR_ENROLLMENT_FAILED",
  "PASSWORD_CHANGE_FAILED",
  "PASSWORD_RESET_FAILED",
  "EMAIL_VERIFICATION_FAILED",
  "EMAIL_CHANGE_FAILED",
  "INVITATION_FAILED",
  "FEDERATION_CONNECT_FAILED",
  "FEDERATION_LINK_DECLINED",
  "BACKCHANNEL_LOGOUT_FAILED",
]);

/** Ops/security trail, not part of a person's activity. */
const TRAIL_ONLY = new Set([
  "CLIENT_REGISTERED",
  "CLIENT_UPDATED",
  "CLIENT_DELETED",
  "BACKCHANNEL_LOGOUT_FAILED",
]);

/** Login steps — only they can carry "new device" or be unfinished. */
const LOGIN_ACTIONS = new Set([
  "LOGIN_SUCCEEDED",
  "LOGIN_FAILED",
  "PASSKEY_LOGIN_SUCCEEDED",
  "PASSKEY_LOGIN_FAILED",
  "MFA_SUCCEEDED",
  "MFA_FAILED",
  "RECOVERY_CODE_USED",
  "RECOVERY_CODE_FAILED",
  "EMAIL_VERIFIED",
  "EMAIL_VERIFICATION_FAILED",
]);

/** Admin actions on someone else's account — read as "received" in that person's log. */
const TWO_SIDED_ACTIONS = new Set([
  "ACCESS_GRANTED",
  "INVITATION_SENT",
  "INVITATION_FAILED",
  "IMPERSONATE_ONLY_SET",
  "INVITATION_REVOKED",
  "PASSWORD_RESET_REVOKED",
  "PASSWORD_SET_BY_ADMIN",
  "PASSWORD_RESET_REQUESTED",
]);

export function isOk(action: string): boolean {
  return !NOT_OK.has(action);
}

export function isTrailOnly(action: string): boolean {
  return TRAIL_ONLY.has(action);
}

// ---------------------------------------------------------------------------
// Words — kept in line with the auth-server's de.json (account.activityLog.*).
// ---------------------------------------------------------------------------

export const ACTION_LABEL: Record<string, string> = {
  LOGIN_SUCCEEDED: "Anmeldung erfolgreich",
  LOGIN_FAILED: "Anmeldung fehlgeschlagen",
  LOGOUT: "Abmeldung",
  PROFILE_SWITCHED: "Profil gewechselt",
  MFA_SUCCEEDED: "Zwei-Faktor-Bestätigung",
  MFA_FAILED: "Zwei-Faktor fehlgeschlagen",
  PASSKEY_LOGIN_SUCCEEDED: "Anmeldung mit Passkey",
  PASSKEY_LOGIN_FAILED: "Passkey-Anmeldung fehlgeschlagen",
  RECOVERY_CODE_USED: "Wiederherstellungscode verwendet",
  RECOVERY_CODE_FAILED: "Wiederherstellungscode falsch",
  TWO_FACTOR_RESET: "Alle Zwei-Faktor-Methoden nach Wiederherstellung entfernt",
  SESSION_REVOKED: "Sitzung beendet",
  SESSIONS_REVOKED_OTHERS: "Alle anderen Sitzungen beendet",
  ACCESS_GRANTED: "Zugang gewährt",
  ACCOUNT_ACTIVATED: "Zugang eingerichtet",
  PASSWORD_CHANGED: "Passwort geändert",
  PASSWORD_CHANGE_FAILED: "Passwortänderung fehlgeschlagen",
  PASSWORD_RESET_REQUESTED: "Zurücksetzen des Passworts angefordert",
  PASSWORD_RESET: "Passwort über E-Mail-Link zurückgesetzt",
  PASSWORD_RESET_CANCELLED: "Zurücksetzen des Passworts verworfen",
  PASSWORD_RESET_FAILED: "Zurücksetzen des Passworts fehlgeschlagen",
  PASSWORD_RESET_REVOKED: "Offenen Reset-Link verworfen",
  PASSWORD_SET_BY_ADMIN: "Passwort durch Support gesetzt",
  EMAIL_VERIFIED: "E-Mail-Adresse bestätigt",
  EMAIL_VERIFICATION_FAILED: "E-Mail-Bestätigung fehlgeschlagen",
  EMAIL_CHANGE_STARTED: "E-Mail-Änderung gestartet",
  EMAIL_CHANGE_CODE_RESENT: "E-Mail-Änderung: Code erneut gesendet",
  EMAIL_CHANGE_CONFIRMED: "E-Mail-Änderung bestätigt",
  EMAIL_CHANGE_CANCELLED: "E-Mail-Änderung abgebrochen",
  EMAIL_CHANGE_FAILED: "E-Mail-Änderung fehlgeschlagen",
  INVITATION_SENT: "Einladung verschickt",
  INVITATION_FAILED: "Einladung fehlgeschlagen",
  INVITATION_REVOKED: "Einladung zurückgezogen",
  IMPERSONATE_ONLY_SET: "Eigene Anmeldung des Kontos deaktiviert",
  TWO_FACTOR_ENROLLED: "Zwei-Faktor eingerichtet",
  TWO_FACTOR_ENROLLMENT_FAILED: "Zwei-Faktor-Einrichtung fehlgeschlagen",
  TWO_FACTOR_REMOVED: "Zwei-Faktor entfernt",
  PASSKEY_REGISTERED: "Passkey registriert",
  PASSKEY_REGISTRATION_FAILED: "Passkey-Registrierung fehlgeschlagen",
  PASSKEY_RENAMED: "Passkey umbenannt",
  PASSKEY_REMOVED: "Passkey entfernt",
  RECOVERY_CODES_REGENERATED: "Wiederherstellungscodes erneuert",
  FEDERATION_CONNECTED: "SSO verknüpft",
  FEDERATION_CONNECT_FAILED: "SSO-Verknüpfung fehlgeschlagen",
  FEDERATION_DISCONNECTED: "SSO getrennt",
  FEDERATION_LINK_DECLINED: "SSO-Verknüpfung abgelehnt",
  CLIENT_REGISTERED: "Anwendung registriert",
  CLIENT_UPDATED: "Anwendung aktualisiert",
  CLIENT_DELETED: "Anwendung gelöscht",
  BACKCHANNEL_LOGOUT_FAILED: "Abmeldung einer App fehlgeschlagen",
};

/** The same actions as the person on the receiving end reads them. */
const RECEIVED_LABEL: Record<string, string> = {
  ACCESS_GRANTED: "Zugang erhalten",
  INVITATION_SENT: "Einladung erhalten",
  INVITATION_FAILED: "Einladung an dieses Konto fehlgeschlagen",
  IMPERSONATE_ONLY_SET: "Eigene Anmeldung durch Support deaktiviert",
  INVITATION_REVOKED: "Einladung durch Support zurückgezogen",
  PASSWORD_RESET_REVOKED: "Reset-Link durch Support verworfen",
  PASSWORD_SET_BY_ADMIN: "Passwort durch Support gesetzt",
  PASSWORD_RESET_REQUESTED: "Reset-Link durch Support verschickt",
};

const REASON_TEXT: Record<string, string> = {
  "current-invalid": "Aktuelles Passwort falsch",
  "invalid-code": "Code falsch oder abgelaufen",
  "delivery-failed": "Zustellung fehlgeschlagen",
  "invalid-link": "Link ungültig oder abgelaufen",
  login: "Anmeldung erfolgreich",
  "password-changed": "Passwort geändert",
  "invalid-password": "Falsches Passwort",
  "invalid-name": "Ungültiger Name",
  "invalid-request": "Ungültige Anfrage",
  "malformed-response": "Ungültige Anfrage",
  "not-verified": "Bestätigung durch das Gerät fehlgeschlagen",
  forbidden: "Keine Berechtigung",
};

/** Which step of an unfinished login had succeeded. */
const STEP_TEXT: Record<string, string> = {
  LOGIN_SUCCEEDED: "Passwort korrekt",
  PASSKEY_LOGIN_SUCCEEDED: "Passkey bestätigt",
  MFA_SUCCEEDED: "Zweiter Faktor bestätigt",
  RECOVERY_CODE_USED: "Wiederherstellungscode verwendet",
  FEDERATED: "Extern angemeldet",
  EMAIL_VERIFIED: "E-Mail-Adresse bestätigt",
};

/** Product names as the auth-server dashboard shows them, not the technical Application names. */
const APPLICATION_NAMES: Record<string, string> = {
  [ApplicationClientIds.Account]: "TK-Login",
  [ApplicationClientIds.Planung]: "TK-Planung",
  [ApplicationClientIds.Bp]: "TK-Bildungsplattform",
  [ApplicationClientIds.BpApp]: "TK-Bildungsplattform",
  [ApplicationClientIds.Iphis]: "TK-Zeugnis",
  [ApplicationClientIds.Admin]: "Schulsuite-Admin",
};

export function applicationName(clientId: string, fallback = ""): string {
  return APPLICATION_NAMES[clientId] ?? fallback;
}

export function auditActionLabel(action: string): string {
  return ACTION_LABEL[action] ?? action;
}

// ---------------------------------------------------------------------------
// Device and ip
// ---------------------------------------------------------------------------

export function parseDevice(userAgent: string | undefined): string {
  if (!userAgent) return "";
  if (/Android/.test(userAgent)) return "Android";
  if (/iPhone|iPad|iOS/.test(userAgent)) return "iOS";
  if (/Macintosh|Mac OS X/.test(userAgent)) return "Mac";
  if (/Windows/.test(userAgent)) return "Windows";
  if (/Linux/.test(userAgent)) return "Linux";
  return "";
}

/** Order matters: Edge, Opera and Samsung also claim "Chrome", Chrome also claims "Safari". */
export function parseBrowser(userAgent: string | undefined): string {
  if (!userAgent) return "";
  if (/Edg(e|A|iOS)?\//.test(userAgent)) return "Edge";
  if (/OPR\/|Opera/.test(userAgent)) return "Opera";
  if (/SamsungBrowser\//.test(userAgent)) return "Samsung Internet";
  if (/Firefox\/|FxiOS\//.test(userAgent)) return "Firefox";
  if (/Chrome\/|CriOS\//.test(userAgent)) return "Chrome";
  if (/Version\/.*Safari\//.test(userAgent)) return "Safari";
  return "";
}

/** "Firefox · Windows" — whatever browser or system is recognisable. */
export function describeDevice(userAgent: string | undefined): string {
  return [parseBrowser(userAgent), parseDevice(userAgent)].filter(Boolean).join(" · ");
}

/** Never the full address: last IPv4 octet / everything after the third IPv6 group hidden. */
export function displayIp(ip: string | undefined): string {
  if (!ip) return "";
  if (ip.includes(":")) {
    return `${ip.split(":").slice(0, 3).join(":")}:…`;
  }
  const parts = ip.split(".");
  return parts.length === 4 ? `${parts.slice(0, 3).join(".")}.x` : ip;
}

// ---------------------------------------------------------------------------
// Mapping
// ---------------------------------------------------------------------------

export function parseMetadata(raw: string | null | undefined): Metadata {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw) as unknown;
    return parsed && typeof parsed === "object" ? (parsed as Metadata) : {};
  } catch {
    return {};
  }
}

function str(m: Metadata, key: string): string {
  const v = m[key];
  return typeof v === "string" ? v : "";
}

const TIME_ZONE = "Europe/Berlin";
const dayKey = (d: Date) =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
const fmtTime = (d: Date) =>
  new Intl.DateTimeFormat("de-DE", { timeZone: TIME_ZONE, timeStyle: "short" }).format(d);
const fmtDateTime = (d: Date) =>
  new Intl.DateTimeFormat("de-DE", {
    timeZone: TIME_ZONE,
    dateStyle: "medium",
    timeStyle: "short",
  }).format(d);
/** "Mittwoch, 16.09.2026" */
export const fmtDay = (d: Date) =>
  new Intl.DateTimeFormat("de-DE", {
    timeZone: TIME_ZONE,
    weekday: "long",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(d);

function unfinishedLogin(e: AuditEventLike): boolean {
  return LOGIN_ACTIONS.has(e.action) && isOk(e.action) && !!e.loginCid && !e.sessionUid;
}

type Subject = { userUuid?: string; profileUuids?: readonly string[] };

/**
 * A two-sided action read from the subject's point of view: someone else did
 * it to the subject's account. The actor is the subject when it is the
 * account itself or one of its profiles. Without the profiles only the event
 * can tell (`by: "support"`, or an actor that is not the account). Without a
 * subject (the global feed) every event reads as its actor doing it.
 */
function foreignAction(e: AuditEventLike, m: Metadata, subject: Subject): boolean {
  if (!subject.userUuid || !TWO_SIDED_ACTIONS.has(e.action)) return false;
  if (subject.profileUuids) {
    const actorIsSubject =
      e.actorType === "User"
        ? e.actorId === subject.userUuid
        : !!e.actorId && subject.profileUuids.includes(e.actorId);
    return !actorIsSubject;
  }
  if (m.by === "support") return true;
  return e.actorId !== subject.userUuid;
}

function labelOf(e: AuditEventLike, m: Metadata, foreign: boolean, unfinished: boolean): string {
  // One action for both directions of the switch; the metadata carries the value.
  if (e.action === "IMPERSONATE_ONLY_SET" && m.impersonateOnly === false) {
    return foreign
      ? "Eigene Anmeldung durch Support aktiviert"
      : "Eigene Anmeldung des Kontos aktiviert";
  }
  if (foreign) return RECEIVED_LABEL[e.action] ?? auditActionLabel(e.action);
  if (unfinished) return "Anmeldung nicht abgeschlossen";
  return auditActionLabel(e.action);
}

function detailOf(
  e: AuditEventLike,
  m: Metadata,
  names: NameMap,
  foreign: boolean,
  unfinished: boolean,
  withTarget: boolean,
): string {
  const parts: string[] = [];
  if (unfinished) {
    parts.push(STEP_TEXT[m.federatedProvider ? "FEDERATED" : e.action] ?? "");
  }
  if (TWO_SIDED_ACTIONS.has(e.action)) {
    // On the acting admin's page the person and their school are the detail.
    // On the person's page the admin is named in the "durch …" note; in the
    // feed the person is the target column and only the school is added.
    if (withTarget) parts.push(str(m, "targetName") || str(m, "targetEmail"));
    if (!foreign) parts.push(str(m, "targetOrganization"));
    if (m.newAccount === true) parts.push("Neuer Zugang angelegt");
  } else if (e.targetType === "PasskeyCredential") {
    parts.push(str(m, "passkeyName") || (e.targetId ? (names.get(e.targetId) ?? "") : ""));
  } else if (e.targetType === "Profile" || e.targetType === "FederatedClient") {
    parts.push(e.targetId ? (names.get(e.targetId) ?? "") : "");
  }
  const newEmail = str(m, "newEmail");
  if (newEmail) {
    const oldEmail = str(m, "oldEmail");
    parts.push(oldEmail ? `${oldEmail} → ${newEmail}` : newEmail);
  }
  parts.push(str(m, "federatedProvider"));
  const clientId = str(m, "clientId");
  if (clientId) parts.push(applicationName(clientId, str(m, "clientName") || clientId));
  const reason = str(m, "reason");
  if (reason) parts.push(REASON_TEXT[reason] ?? "");
  return parts.filter(Boolean).join(" · ");
}

/**
 * Logins whose browser/system combination did not occur earlier in the
 * window. Expects newest first. The ip deliberately does not count (mobile
 * networks change it constantly); the oldest login in the window has nothing
 * to compare with; an admin's device does not count as the person's.
 */
function newDeviceFlags(
  events: AuditEventLike[],
  metas: Metadata[],
  foreign: boolean[],
): boolean[] {
  const flags = events.map(() => false);
  const seen = new Set<string>();
  for (let i = events.length - 1; i >= 0; i--) {
    const device = describeDevice(str(metas[i] as Metadata, "userAgent"));
    if (!device || foreign[i]) continue;
    flags[i] =
      LOGIN_ACTIONS.has((events[i] as AuditEventLike).action) && seen.size > 0 && !seen.has(device);
    seen.add(device);
  }
  return flags;
}

export function toActivityItems(
  events: readonly AuditEventLike[],
  opts: { names: NameMap; subjectUserUuid?: string; subjectProfileUuids?: readonly string[] },
): ActivityItem[] {
  const subject: Subject = {
    userUuid: opts.subjectUserUuid,
    profileUuids: opts.subjectProfileUuids,
  };
  const shown = opts.subjectUserUuid ? events.filter((e) => !isTrailOnly(e.action)) : [...events];
  const metas = shown.map((e) => parseMetadata(e.metadata));
  const foreign = shown.map((e, i) => foreignAction(e, metas[i] as Metadata, subject));
  const newDevice = newDeviceFlags(shown, metas, foreign);
  return shown.map((e, i) => {
    const m = metas[i] as Metadata;
    const unfinished = unfinishedLogin(e);
    const date = new Date(e.occurredAt);
    const valid = Number.isFinite(date.getTime());
    const isForeign = foreign[i] as boolean;
    return {
      id: e.id,
      ok: isOk(e.action) && !unfinished,
      unfinished,
      foreign: isForeign,
      label: labelOf(e, m, isForeign, unfinished),
      detail: detailOf(e, m, opts.names, isForeign, unfinished, !isForeign && !!subject.userUuid),
      note: isForeign ? `durch ${str(m, "actorName") || "Support"}` : "",
      device: isForeign ? "" : describeDevice(str(m, "userAgent")),
      ip: isForeign ? "" : displayIp(str(m, "ip")),
      newDevice: newDevice[i] as boolean,
      day: valid ? dayKey(date) : "",
      time: valid ? fmtTime(date) : "",
      when: valid ? fmtDateTime(date) : e.occurredAt,
      occurredAt: e.occurredAt,
      raw: e,
    };
  });
}

export type DayGroup = { day: string; label: string; items: ActivityItem[] };

/** Newest day first; items keep their (newest-first) order. */
export function groupByDay(items: readonly ActivityItem[]): DayGroup[] {
  const groups: DayGroup[] = [];
  for (const item of items) {
    const last = groups.at(-1);
    if (last?.day === item.day) {
      last.items.push(item);
    } else {
      groups.push({ day: item.day, label: fmtDay(new Date(item.occurredAt)), items: [item] });
    }
  }
  return groups;
}
